// WorkflowView.tsx - Ultra Simplified 2-Step Version

import React, { useState, useEffect } from 'react';
import { 
  Firestore,
  doc,
  updateDoc
} from 'firebase/firestore';
import { 
  ref,
  uploadBytes,
  getDownloadURL,
  getStorage
} from 'firebase/storage';
import { Functions, httpsCallable } from 'firebase/functions';
import { App, Theme, ThemeConfig } from '../AppManager';
import { imageProcessor } from '../generator/imageProcessor';
import { getLocationById } from '../config/locationConfig';
import { useLocations } from '../../../../hooks/useLocations';
import './WorkflowView.css';

interface WorkflowViewProps {
  db: Firestore;
  storage: ReturnType<typeof getStorage>;
  functions: Functions;
  appData: App | null;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
  setLoading: (loading: boolean) => void;
  onBack: () => void;
  onAppDataUpdate: () => void;
  selectedOrganizationId: string;
}

const WorkflowView: React.FC<WorkflowViewProps> = ({
  db,
  storage,
  functions,
  appData,
  onError,
  onSuccess,
  setLoading,
  onBack,
  onAppDataUpdate,
  selectedOrganizationId
}) => {
  const { getOrganizationById } = useLocations();
  
  // App details state
  const [appName, setAppName] = useState<string>('');
  const [appDescription, setAppDescription] = useState<string>('');
  const [appId, setAppId] = useState<string>('');
  const [appType, setAppType] = useState<'single-store' | 'single-store-multi-location' | 'multi-store'>('single-store');
  
  // Icon upload state
  const [selectedIcon, setSelectedIcon] = useState<File | null>(null);
  const [iconPreview, setIconPreview] = useState<string>('');
  const [iconError, setIconError] = useState<string>('');
  
  // Logo upload state
  const [selectedLogo, setSelectedLogo] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [logoError, setLogoError] = useState<string>('');
  
  // Theme configuration state
  const [selectedTheme, setSelectedTheme] = useState<'modern-light' | 'modern-dark'>('modern-light');
  const [accentColor, setAccentColor] = useState<string>('#4169e1');
  
  // Workflow state
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Cloud function reference
  const submitAppConfigurationFunc = httpsCallable(functions, 'submitAppConfiguration');

  // Helper function to get the correct location ID
  const getCorrectLocationId = (): string => {
    if (selectedOrganizationId) {
      const orgLocation = getOrganizationById(selectedOrganizationId);
      if (orgLocation) {
        return orgLocation.id;
      }
    }
    
    if (appData?.locationId) {
      return appData.locationId;
    }
    
    return 'uk-london';
  };

  // Update custom ID when app name changes
  useEffect(() => {
    if (appName && appData && appData.status === 'new') {
      const baseId = appName
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-');
      
      setAppId(baseId);
    }
  }, [appName, appData?.status]);

  // Initialize state from app data
  useEffect(() => {
    if (!appData) return;

    setAppName(appData.name || '');
    setAppDescription(appData.description || '');
    setAppType((appData.type as any) || 'single-store');

    // For new apps, initialize with temp ID suffix
    if (appData.status === 'new' && appData.id.includes('temp-')) {
      setAppId(''); // Will be filled when name is entered
    } else if (appData.id) {
      // For existing apps, use the full ID
      setAppId(appData.id);
    }

    if (appData.theme) {
      setSelectedTheme(appData.theme.variant);
      setAccentColor(appData.theme.accentColor);
    }

    // If app is already configured, go to step 2
    if (appData.status === 'configured' || appData.status === 'ready-for-deployment') {
      setCurrentStep(2);
    }
  }, [appData]);

  if (!appData) return null;

  // Check if configuration is complete based on status
  const isConfigurationComplete = appData.status === 'configured' || appData.status === 'ready-for-deployment';

  // Image validation and preview handlers
  const handleImageSelect = (
    event: React.ChangeEvent<HTMLInputElement>, 
    type: 'icon' | 'logo'
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validation = imageProcessor.validateImage(file);
    if (!validation.valid) {
      if (type === 'icon') {
        setIconError(validation.error || 'Invalid image file');
      } else {
        setLogoError(validation.error || 'Invalid image file');
      }
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const preview = e.target?.result as string;
      if (type === 'icon') {
        setSelectedIcon(file);
        setIconPreview(preview);
        setIconError('');
      } else {
        setSelectedLogo(file);
        setLogoPreview(preview);
        setLogoError('');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleImageDrop = (
    event: React.DragEvent<HTMLDivElement>, 
    type: 'icon' | 'logo'
  ) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      const input = document.createElement('input');
      input.type = 'file';
      input.files = event.dataTransfer.files;
      
      const changeEvent = new Event('change', { bubbles: true });
      Object.defineProperty(changeEvent, 'target', {
        writable: false,
        value: input
      });
      
      handleImageSelect(changeEvent as any, type);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleRemoveImage = (type: 'icon' | 'logo') => {
    if (type === 'icon') {
      setSelectedIcon(null);
      setIconPreview('');
      setIconError('');
    } else {
      setSelectedLogo(null);
      setLogoPreview('');
      setLogoError('');
    }
  };

  // Theme handlers - removed custom theme handlers

  // Validation
  const isStep1Valid = () => {
    return (
      appName.trim() !== '' &&
      appId.trim() !== '' &&
      appDescription.trim() !== '' &&
      selectedIcon !== null &&
      selectedLogo !== null
    );
  };

  // Submit configuration
  const handleSubmitConfiguration = async () => {
    if (!isStep1Valid()) {
      onError('Please fill in all required fields.');
      return;
    }

    setIsSubmitting(true);
    setLoading(true);

    try {
      // Upload icon
      const iconRef = ref(storage, `app-icons/${appData.id}/${selectedIcon!.name}`);
      const iconUpload = await uploadBytes(iconRef, selectedIcon!);
      const iconUrl = await getDownloadURL(iconUpload.ref);

      // Upload logo  
      const logoRef = ref(storage, `app-logos/${appData.id}/${selectedLogo!.name}`);
      const logoUpload = await uploadBytes(logoRef, selectedLogo!);
      const logoUrl = await getDownloadURL(logoUpload.ref);

      // Prepare theme config
      const themeConfig: ThemeConfig = {
        variant: selectedTheme,
        accentColor: accentColor
      };

      // Use the app ID directly (no prefix)
      const finalAppId = appId.trim();

      // Call cloud function with all data - this creates the app AND configures it
      const result = await submitAppConfigurationFunc({
        appId: finalAppId,
        appDetails: {
          name: appName.trim(),
          description: appDescription.trim(),
          type: appType,
        },
        appIcon: iconUrl,
        appLogo: logoUrl,
        theme: themeConfig,
        locationId: getCorrectLocationId(),
        organizationId: selectedOrganizationId,
        region: appData?.region || 'europe-west2',
        originalTempId: appData?.id // Send the temp ID so cloud function can clean up if needed
      });

      // @ts-ignore
      if (result.data && result.data.status === "SUCCESS") {
        // The cloud function created the app AND configured it
        // No need to update local document since it will be loaded fresh
        
        onAppDataUpdate();
        onSuccess('App created and configured successfully!');
        setCurrentStep(2);
      } else {
        // @ts-ignore
        onError(result.data?.message || 'Failed to create and configure app. Please try again.');
      }
    } catch (err: any) {
      console.error('Error submitting configuration:', err);
      onError(err.message || 'Failed to create and configure app. Please try again.');
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  return (
    <div className="workflow-view">
      <div className="workflow-header">
        <div className="workflow-header-left">
          <button 
            type="button"
            className="workflow-back-btn" 
            onClick={onBack}
          >
            ← Back to Apps
          </button>
        </div>
        
        <div className="workflow-progress">
          <div className={`workflow-progress-step ${currentStep >= 1 ? 'active' : ''} ${isConfigurationComplete ? 'completed' : ''}`}>
            <div className="workflow-step-number">{isConfigurationComplete ? '✓' : '1'}</div>
            <span>App Configuration</span>
          </div>
          <div className="workflow-progress-line"></div>
          <div className={`workflow-progress-step ${currentStep >= 2 ? 'active' : ''}`}>
            <div className="workflow-step-number">2</div>
            <span>Add First Store</span>
          </div>
        </div>
      </div>
      
      <div className="workflow-status">

        {currentStep === 1 && (
          <div className="workflow-configuration-step">
            <h3>Complete Your App Configuration</h3>
            <p>Provide all the details needed to set up your ordering app.</p>

            <div className="workflow-config-form">
              {/* App Details Section */}
              <div className="workflow-config-section">
                <h4>App Details</h4>
                <div className="workflow-form-group">
                  <label>App Name *</label>
                  <input
                    type="text"
                    value={appName}
                    onChange={(e) => setAppName(e.target.value)}
                    placeholder="Enter your app name"
                    className="workflow-form-input"
                  />
                </div>
                <div className="workflow-form-group">
                  <label>App ID *</label>
                  <input
                    type="text"
                    value={appId}
                    onChange={(e) => setAppId(e.target.value)}
                    placeholder="Enter app ID"
                    className="workflow-form-input"
                  />
                </div>
                <div className="workflow-form-group">
                  <label>App Type *</label>
                  <select
                    value={appType}
                    onChange={(e) => setAppType(e.target.value as any)}
                    className="workflow-form-input"
                  >
                    <option value="single-store">Single Store</option>
                    <option value="single-store-multi-location">Single Store Multi Location</option>
                    <option value="multi-store">Multi Store</option>
                  </select>
                </div>
                <div className="workflow-form-group">
                  <label>App Description *</label>
                  <textarea
                    value={appDescription}
                    onChange={(e) => setAppDescription(e.target.value)}
                    placeholder="Describe what your app does"
                    className="workflow-form-textarea"
                    rows={3}
                  />
                </div>
              </div>

              {/* Images Section */}
              <div className="workflow-config-section">
                <h4>App Images</h4>
                <div className="workflow-images-grid">
                  {/* Icon Upload */}
                  <div className="workflow-image-upload-group">
                    <label>App Icon * (Square, 512x512px min)</label>
                    {!selectedIcon ? (
                      <div 
                        className="workflow-image-upload-area"
                        onDrop={(e) => handleImageDrop(e, 'icon')}
                        onDragOver={handleDragOver}
                        onClick={() => document.getElementById('icon-input')?.click()}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                          <circle cx="8.5" cy="8.5" r="1.5"/>
                          <polyline points="21,15 16,10 5,21"/>
                        </svg>
                        <p>Upload App Icon</p>
                        <small>PNG, JPEG, JPG, or WebP (max 5MB)</small>
                      </div>
                    ) : (
                      <div className="workflow-image-preview">
                        <img src={iconPreview} alt="App icon preview" />
                        <button onClick={() => handleRemoveImage('icon')}>Remove</button>
                      </div>
                    )}
                    <input
                      id="icon-input"
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageSelect(e, 'icon')}
                      style={{ display: 'none' }}
                    />
                    {iconError && <div className="workflow-error-text">{iconError}</div>}
                  </div>

                  {/* Logo Upload */}
                  <div className="workflow-image-upload-group">
                    <label>App Logo * (Any ratio)</label>
                    {!selectedLogo ? (
                      <div 
                        className="workflow-image-upload-area"
                        onDrop={(e) => handleImageDrop(e, 'logo')}
                        onDragOver={handleDragOver}
                        onClick={() => document.getElementById('logo-input')?.click()}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                          <circle cx="8.5" cy="8.5" r="1.5"/>
                          <polyline points="21,15 16,10 5,21"/>
                        </svg>
                        <p>Upload App Logo</p>
                        <small>PNG, JPEG, JPG, or WebP (max 5MB)</small>
                      </div>
                    ) : (
                      <div className="workflow-image-preview">
                        <img src={logoPreview} alt="App logo preview" />
                        <button onClick={() => handleRemoveImage('logo')}>Remove</button>
                      </div>
                    )}
                    <input
                      id="logo-input"
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageSelect(e, 'logo')}
                      style={{ display: 'none' }}
                    />
                    {logoError && <div className="workflow-error-text">{logoError}</div>}
                  </div>
                </div>
              </div>

              {/* Theme Section */}
              <div className="workflow-config-section">
                <h4>App Theme</h4>
                <div className="workflow-default-theme-content">
                  <div className="workflow-theme-selection">
                    <label>Theme Variant:</label>
                    <div className="workflow-theme-toggle">
                      <button
                        type="button"
                        className={`workflow-theme-toggle-btn ${selectedTheme === 'modern-light' ? 'active' : ''}`}
                        onClick={() => setSelectedTheme('modern-light')}
                      >
                        Light
                      </button>
                      <button
                        type="button"
                        className={`workflow-theme-toggle-btn ${selectedTheme === 'modern-dark' ? 'active' : ''}`}
                        onClick={() => setSelectedTheme('modern-dark')}
                      >
                        Dark
                      </button>
                    </div>
                  </div>
                  
                  <div className="workflow-color-selection">
                    <label>Primary Accent Color:</label>
                    <div className="workflow-color-input-group">
                      <input
                        type="color"
                        value={accentColor}
                        onChange={(e) => setAccentColor(e.target.value)}
                        className="workflow-color-picker"
                      />
                      <input
                        type="text"
                        value={accentColor}
                        onChange={(e) => setAccentColor(e.target.value)}
                        placeholder="#4169e1"
                        className="workflow-color-text-input"
                        pattern="^#[0-9A-Fa-f]{6}$"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="workflow-config-actions">
                <button
                  type="button"
                  className="workflow-submit-config-btn"
                  onClick={handleSubmitConfiguration}
                  disabled={!isStep1Valid() || isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <div className="workflow-loading-spinner-small"></div>
                      Submitting Configuration...
                    </>
                  ) : (
                    'Complete App Configuration'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className="workflow-store-step">
            <h3>Add Your First Store</h3>
            <p>Now let's set up your first store location.</p>
            
            {/* TODO: Store configuration will go here */}
            <div className="workflow-placeholder-content">
              <div className="workflow-placeholder-icon">🏪</div>
              <h4>Store Setup Coming Soon</h4>
              <p>The store configuration interface will be implemented here.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkflowView;